/**
 * Масштаб рабочей области раздела.
 *
 * В журнале четырнадцать колонок, и на ноутбуке они не помещаются, а на
 * большом мониторе строки, наоборот, редкие. Масштаб решает и то и другое,
 * причём по-разному на разных рабочих местах — поэтому он запоминается
 * в браузере, а не в настройках лаборатории: это свойство ЭКРАНА, а не
 * общего рабочего пространства.
 *
 * У КАЖДОГО раздела масштаб свой. Журнал заказов смотрят мелко, чтобы
 * увидеть разом весь месяц, а прайс-лист или карточку сотрудника — наоборот,
 * покрупнее. Один общий масштаб заставлял бы переставлять его при каждом
 * переходе между разделами.
 *
 * Применяется css-свойством `zoom`, а не `transform: scale`. Оба уменьшают
 * содержимое, но `transform` не меняет занимаемое место: таблица съёжилась
 * бы, оставив вокруг себя пустоту прежнего размера, и горизонтальная
 * прокрутка считалась бы по старой ширине. `zoom` пересчитывает раскладку
 * по-настоящему.
 */

import { useCallback, useEffect, useState } from 'react';

/** Раздел, чей масштаб настраивается: от него зависит ключ хранения. */
export type ZoomArea =
  | 'orders'
  | 'doctors'
  | 'clinics'
  | 'constructions'
  | 'documents'
  | 'employees'
  | 'finance';

/**
 * Пределы масштаба.
 *
 * Нижняя граница была 60 % — «ниже текст нечитаем». Но читать при сильном
 * уменьшении и не собираются: так смотрят на журнал целиком, чтобы увидеть
 * разом весь месяц и понять, где что скопилось, а нужную строку потом
 * возвращают крупной. Запрещать это значило решать за человека, зачем он
 * смотрит на свой журнал.
 */
export const ZOOM_MIN = 20;
export const ZOOM_MAX = 140;
export const ZOOM_STEP = 10;

function clamp(value: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value / ZOOM_STEP) * ZOOM_STEP));
}

/**
 * Масштаб по умолчанию.
 *
 * В заказах на телефоне — 80 %: журнал в четырнадцать колонок и так
 * прокручивается вбок, и чем больше умещается за раз, тем меньше этой
 * прокрутки. В остальных разделах и на настольном экране мельчить незачем.
 *
 * Это именно НАЧАЛЬНОЕ значение: выбранное вручную запоминается и заменяет
 * его. Масштаб хранится в браузере, а телефон — другой браузер, так что
 * настольная настройка от этого не меняется.
 */
function defaultZoom(область: ZoomArea): number {
  if (область !== 'orders') return 100;
  try {
    return window.matchMedia('(max-width: 768px)').matches ? 80 : 100;
  } catch {
    return 100;
  }
}

function stored(область: ZoomArea): number {
  // Битое или чужое значение не должно ломать экран — молча берём обычный.
  try {
    const raw = window.localStorage.getItem(`otc.${область}.zoom`);
    if (raw === null) return defaultZoom(область);
    const value = Number(raw);
    return Number.isFinite(value) ? clamp(value) : defaultZoom(область);
  } catch {
    return defaultZoom(область);
  }
}

/**
 * Широкий ли экран прямо сейчас.
 *
 * Та же граница, что у `md:` в разметке, — иначе кнопка, спрятанная стилями,
 * и масштаб, применённый скриптом, разошлись бы: управление исчезло, а
 * содержимое осталось уменьшенным, и вернуть его было бы нечем.
 */
function useWideScreen(): boolean {
  const [широкий, setШирокий] = useState(() => {
    try {
      return window.matchMedia('(min-width: 768px)').matches;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(min-width: 768px)');
    } catch {
      return;
    }
    const слушать = () => setШирокий(mq.matches);
    слушать();
    mq.addEventListener('change', слушать);
    return () => mq.removeEventListener('change', слушать);
  }, []);

  return широкий;
}

export function useTableZoom(
  область: ZoomArea = 'orders',
  /**
   * Только на широком экране.
   *
   * На телефоне масштаб нужен лишь в журнале заказов: там четырнадцать
   * колонок и длинный список, и уменьшение — единственный способ увидеть
   * за раз больше трёх карточек. В остальных разделах на телефоне и так
   * одна колонка: кнопка отнимала место в шапке, ничего не давая взамен.
   */
  толькоШирокий = false,
) {
  const [zoom, setZoom] = useState(() => stored(область));
  const широкий = useWideScreen();

  useEffect(() => {
    try {
      window.localStorage.setItem(`otc.${область}.zoom`, String(zoom));
    } catch {
      // Приватный режим или переполненное хранилище — масштаб просто не
      // переживёт перезагрузку. Ронять из-за этого экран незачем.
    }
  }, [zoom, область]);

  const zoomBy = useCallback((delta: number) => setZoom((value) => clamp(value + delta)), []);
  const reset = useCallback(() => setZoom(100), []);

  /*
   * На узком экране отдаём ровно 100 %, а не сохранённое значение: иначе
   * настольная настройка поехала бы на телефон через общий ключ хранения,
   * если человек открыл программу в том же браузере на планшете и повернул
   * его в портрет.
   */
  const действующий = толькоШирокий && !широкий ? 100 : zoom;

  return {
    zoom: действующий,
    zoomBy,
    reset,
    canZoomOut: действующий > ZOOM_MIN,
    canZoomIn: действующий < ZOOM_MAX,
    /** Показывать ли переключатель: на телефоне в этих разделах его нет. */
    доступен: !толькоШирокий || широкий,
  };
}
